import { PersistentRailAvatar } from "@/components/AccountPopover/PersistentRailAvatar";
import {
  useSidebarContext,
  WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH,
  WORKSPACE_RAIL_DEFAULT_WIDTH,
  WORKSPACE_RAIL_MAX_WIDTH,
  WORKSPACE_RAIL_MIN_WIDTH,
  WORKSPACE_RAIL_MOBILE_NAV_WIDTH,
  type WorkspaceRail,
} from "@/context/SidebarContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { PreviewTooltipProvider } from "@/layouts/WorkspaceLayout/components/PreviewTooltipProvider";
import { RailDrawer } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailDrawer";
import { RailHead } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/RailHead";
import { WorkspaceRailContent } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceRailContent";
import { WorkspaceSidebarFooter } from "@/layouts/WorkspaceLayout/components/WorkspaceSidebarFooter/WorkspaceSidebarFooter";
import { cn } from "@/utils";
import { ResizablePanel } from "@cohesive/ui-components";
import { useEffect, useRef, useState } from "react";

interface WorkspaceLeftRailProps {
  className?: string;
}

function RailInner({ rail }: { rail: WorkspaceRail }) {
  return (
    // One shared tooltip provider per rail so row hover-previews share warm
    // state: first preview delayed, neighbors instant while sliding.
    <PreviewTooltipProvider>
      {/* The docked full-height pane (DS left-pane spec): window-height on
          the rail surface, the panel's border-r as the structural edge. */}
      <div
        data-workspace-rail
        data-onboarding-target="rail-pane"
        className="flex h-full w-full flex-col bg-surface-rail select-none"
        // Suppress the native browser context menu across the rail. Rows/headers
        // with their own right-click menu stop propagation in their onContextMenu,
        // so their events never reach here; everything else bubbles up and is
        // swallowed. Editable fields (e.g. the canvas search) keep the native menu
        // so copy/paste still works.
        onContextMenu={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("input, textarea, [contenteditable]")) return;
          e.preventDefault();
        }}
      >
        {/* The nav unit + the search row (create glyph + search) — the
            pane's head. */}
        <RailHead />
        {/* The region the head and footer leave behind, and the drawer's
            frame of reference: sizing the drawer against the whole pane
            meant its ceiling could never clear the head, so dragging it
            open stopped partway down the list. Measured here, 100% means
            exactly "up to the search row". */}
        <div className="flex min-h-0 flex-1 flex-col [container-type:size]">
          <WorkspaceRailContent rail={rail} />
          {/* The active container's drawer — docked above the footer. */}
          <RailDrawer />
        </div>
        <WorkspaceSidebarFooter />
      </div>
    </PreviewTooltipProvider>
  );
}

export function WorkspaceLeftRail({ className }: WorkspaceLeftRailProps) {
  const lg = useBreakpoint("lg");
  const {
    railOpen,
    activeRail,
    isDrawerOpen,
    setIsDrawerOpen,
    railWidth,
    setRailWidth,
    applyRailWidthToNavZone,
    setRailResizing,
  } = useSidebarContext();

  // The collapsed pane is width-clipped, not unmounted, so its controls
  // stay keyboard-reachable behind the window edge unless the subtree is
  // taken out of the tab order too (DS 021's a11y finding). The flip is
  // DEFERRED to the end of the 300ms slide: dropping the whole subtree
  // from the AX tree on frame one reads as a first-frame hitch. The blur
  // runs immediately so a focused control is released to the document
  // rather than trapped going inert; opening un-inerts at once.
  const paneRef = useRef<HTMLDivElement>(null);
  const [paneInert, setPaneInert] = useState(!railOpen);
  useEffect(() => {
    if (railOpen) {
      setPaneInert(false);
      return;
    }
    const el = document.activeElement;
    if (el instanceof HTMLElement && paneRef.current?.contains(el)) el.blur();
    const timer = window.setTimeout(() => setPaneInert(true), 300);
    return () => window.clearTimeout(timer);
  }, [railOpen]);

  if (!lg) {
    if (!isDrawerOpen) return null;
    return (
      <>
        <div
          className="fixed inset-x-0 bottom-0 top-[var(--app-header-block)] z-30 bg-n-95/50 dark:bg-n-00/50"
          onClick={() => setIsDrawerOpen(false)}
          aria-hidden
        />
        <aside
          className={cn(
            "fixed bottom-0 left-0 top-[var(--app-header-block)] z-40 w-64 border-r border-n-10 bg-surface-rail",
            className,
          )}
        >
          <RailInner rail={activeRail} />
        </aside>
      </>
    );
  }

  return (
    <>
      <ResizablePanel
        direction="right"
        isClosed={!railOpen}
        initialWidth={WORKSPACE_RAIL_DEFAULT_WIDTH}
        minWidth={WORKSPACE_RAIL_MIN_WIDTH}
        maxWidth={WORKSPACE_RAIL_MAX_WIDTH}
        storageKey="workspace-rail-width"
        showHandleIndicator
        className={cn(
          "h-full shrink-0 bg-surface-rail border-r overflow-visible",
          className,
        )}
        onWidthChange={setRailWidth}
        onWidthChangeEvent={applyRailWidthToNavZone}
        onResizeStart={() => {
          applyRailWidthToNavZone(railWidth);
          setRailResizing(true);
        }}
        onResizeEnd={() => setRailResizing(false)}
      >
        <div ref={paneRef} inert={paneInert} className="h-full">
          <RailInner rail={activeRail} />
        </div>
      </ResizablePanel>

      {/* The identity avatar that survives collapse — a sibling of the
          pane, pinned to the footer avatar's exact coordinates, so closing
          the panel wipes the rail out from behind it. */}
      <PersistentRailAvatar />
    </>
  );
}

/** Width of the header nav zone — tracks rail open state and width. */
export function useWorkspaceNavZoneWidth(): number {
  const lg = useBreakpoint("lg");
  const { railOpen, railWidth, isDrawerOpen } = useSidebarContext();

  if (!lg) {
    return isDrawerOpen
      ? WORKSPACE_RAIL_DEFAULT_WIDTH
      : WORKSPACE_RAIL_MOBILE_NAV_WIDTH;
  }
  return railOpen ? railWidth : WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH;
}

/**
 * Horizontal space the rail column actually occupies in the layout row.
 * 0 when collapsed on desktop (the panel closes to zero width) and on mobile
 * (the drawer overlays instead of occupying the row). Unlike the nav zone
 * width, this is the right value to subtract when sizing a pane that should
 * fill the rest of the row.
 */
export function useWorkspaceLeftRailLayoutWidth(): number {
  const lg = useBreakpoint("lg");
  const { railOpen, railWidth } = useSidebarContext();

  if (!lg) {
    return 0;
  }
  return railOpen ? railWidth : 0;
}
